import { z } from 'zod';
import type { SystemRole } from '../system-roles/types';

/** How a person reaches a unit: they work for it, they were granted access,
 *  or their role holds All Business Units (`business_unit.all_units`). */
export const UnitAccessSchema = z.enum(['primary', 'granted', 'all-units']);
export type UnitAccess = z.infer<typeof UnitAccessSchema>;

/** A business unit, and how one person stands in it. */
export const BusinessUnitStandingSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  code: z.string(),
  isDefault: z.boolean(),
  access: UnitAccessSchema,
  systemRoleId: z.string().uuid(),
  systemRoleName: z.string(),
  /** Their role's permissions in this unit — `/users/me` only: the screens
   *  offer, per record, what that record's unit allows. */
  permissions: z
    .array(z.object({ permission: z.string(), scope: z.string().optional() }))
    .optional(),
});
export type BusinessUnitStanding = z.infer<typeof BusinessUnitStandingSchema>;

/** What a unit holds, counted. */
export const UnitCountsSchema = z.object({
  /** Active employees (it is their primary employer). */
  employees: z.number(),
  /** Active people from other units granted access to it. */
  granted: z.number(),
  customers: z.number(),
  activeCustomers: z.number(),
  /** Its own active roles. */
  roles: z.number(),
});
export type UnitCounts = z.infer<typeof UnitCountsSchema>;

/** A unit as the Business Units list and a unit's page show it. */
export const UnitBasicsSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  code: z.string(),
  isDefault: z.boolean(),
});
export type UnitBasics = z.infer<typeof UnitBasicsSchema>;

/** A row of the Business Units list. */
export const BusinessUnitListItemSchema = UnitBasicsSchema.extend({
  counts: UnitCountsSchema,
});
export type BusinessUnitListItem = z.infer<typeof BusinessUnitListItemSchema>;

export const BusinessUnitSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  name: z.string(),
  code: z.string(),
  isDefault: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type BusinessUnit = z.infer<typeof BusinessUnitSchema>;

/** A unit's page: its details, what it holds, and who manages it. */
export const UnitSummarySchema = z.object({
  unit: UnitBasicsSchema.extend({ createdAt: z.string() }),
  counts: UnitCountsSchema,
  /** Active people whose role here can edit roles, and the Super Admin. */
  managers: z.array(
    z.object({
      userId: z.string().uuid(),
      name: z.string(),
      email: z.string(),
      roleName: z.string(),
      access: UnitAccessSchema,
    }),
  ),
});
export type UnitSummary = z.infer<typeof UnitSummarySchema>;

export const CreateBusinessUnitInput = z.object({
  name: z.string().trim().min(1, 'Required').max(120),
  code: z
    .string()
    .trim()
    .min(1, 'Required')
    .max(10, 'At most 10 characters')
    .regex(/^[A-Za-z0-9]+$/, 'Letters and digits only'),
});
export type CreateBusinessUnitInput = z.infer<typeof CreateBusinessUnitInput>;
export type PatchBusinessUnitInput = Partial<CreateBusinessUnitInput>;

export interface GrantAccessInput {
  userId: string;
  systemRoleId: string;
}

export interface ChangePrimaryEmployerInput {
  userId: string;
  systemRoleId: string;
  /** Keep access to the unit they leave, with this role of it. */
  keepPreviousWithRoleId?: string | null;
}

export type UnitRole = SystemRole;
